import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, ShieldAlert, Share2, MoreVertical, Download, Smartphone } from 'lucide-react';
import Button from './ui/Button';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { usePwaInstall } from '../hooks/usePwaInstall';

const DISMISS_KEY = 'notificationPromptDismissed';

const InstallInstructions = ({ platform, isSafari, canInstall, isInstalled, promptInstall }) => {
  if (isInstalled) return null;

  if (canInstall) {
    return (
      <div className="bg-white/5 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-300">
            Instale o app na sua tela inicial para acesso rápido e uma experiência melhor.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={promptInstall} className="mt-3 w-full">
          <Download className="w-4 h-4 mr-2" />
          Instalar app
        </Button>
      </div>
    );
  }

  if (platform === 'ios') {
    return (
      <div className="bg-white/5 rounded-xl p-4 mb-4 flex gap-3">
        <Share2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          {isSafari ? (
            <p>
              Para instalar o app, toque no botão <span className="font-medium text-white">Compartilhar</span> na
              barra do Safari e depois em <span className="font-medium text-white">"Adicionar à Tela de Início"</span>.
            </p>
          ) : (
            <p>
              Abra este site no <span className="font-medium text-white">Safari</span> para poder instalar o app.
              Depois toque em <span className="font-medium text-white">Compartilhar</span> e em{' '}
              <span className="font-medium text-white">"Adicionar à Tela de Início"</span>.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (platform === 'android') {
    return (
      <div className="bg-white/5 rounded-xl p-4 mb-4 flex gap-3">
        <MoreVertical className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-300">
          Toque no menu (⋮) do navegador e selecione{' '}
          <span className="font-medium text-white">"Instalar app"</span> ou{' '}
          <span className="font-medium text-white">"Adicionar à tela inicial"</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 rounded-xl p-4 mb-4 flex gap-3">
      <MoreVertical className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-gray-300">
        Clique no ícone de instalação na barra de endereço do navegador, ou abra o menu (⋮) e selecione{' '}
        <span className="font-medium text-white">"Instalar Revolution"</span>.
      </p>
    </div>
  );
};

const NotificationPermissionModal = () => {
  const { supported, permission, subscribed, loading, enable } = usePushNotifications();
  const { canInstall, isInstalled, promptInstall, platform, isSafari } = usePwaInstall();
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState('');

  const needsNotifications = supported && !subscribed;
  const needsInstall = !isInstalled;

  useEffect(() => {
    if (!needsNotifications && !needsInstall) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsNotifications, needsInstall]);

  useEffect(() => {
    if (!needsNotifications && !needsInstall) setVisible(false);
  }, [needsNotifications, needsInstall]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const handleEnable = async () => {
    setError('');
    try {
      const granted = await enable();
      if (!granted) {
        setError('Permissão não concedida. Você pode ativar depois pelo seu perfil.');
      }
    } catch (err) {
      console.error('Erro ao ativar notificações:', err);
      setError('Não foi possível ativar as notificações. Tente novamente mais tarde.');
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={dismiss}
          />

          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl max-w-md w-full mx-4 p-6"
            >
              <button
                onClick={dismiss}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-4 mb-4">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur-lg opacity-50"></div>
                  <div className="relative w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Aproveite tudo do Revolution</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Instale o app e ative as notificações para não perder eventos e convocações do clã.
                  </p>
                </div>
              </div>

              {needsInstall && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Instalar o app</p>
                  <InstallInstructions
                    platform={platform}
                    isSafari={isSafari}
                    canInstall={canInstall}
                    isInstalled={isInstalled}
                    promptInstall={promptInstall}
                  />
                </>
              )}

              {needsNotifications && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Notificações</p>
                  {permission === 'denied' ? (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4 flex gap-3">
                      <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-200">
                        <p className="font-medium mb-1">As notificações estão bloqueadas no navegador.</p>
                        <p>
                          Para ativar, clique no ícone de cadeado/informações ao lado do endereço do site,
                          procure por "Notificações" e altere para "Permitir". Depois recarregue a página.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white/5 rounded-xl p-4 mb-4 flex gap-3">
                      <Bell className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-gray-300">
                        <p>Ao clicar em "Ativar notificações", seu navegador vai pedir uma permissão. Basta confirmar.</p>
                        <p className="text-gray-400 mt-1">Você pode desativar quando quiser em Perfil &gt; Notificações.</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && (
                <p className="text-sm text-red-400 mb-4">{error}</p>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={dismiss} disabled={loading}>
                  Agora não
                </Button>
                {needsNotifications && permission !== 'denied' && (
                  <Button variant="primary" onClick={handleEnable} loading={loading}>
                    Ativar notificações
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NotificationPermissionModal;
